import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TenantService } from './tenant.service';

/**
 * One entry in `frontend_urls[]` returned by the spec'd
 * `GET /v1/tenant/stores/{store}/frontend-urls` endpoint.
 *
 * `source` lets the picker distinguish a frontend that serves only this
 * store (`frontend_site_direct`) from a multi-store frontend that this
 * store happens to be one of (`frontend_site_sub_store`).
 */
export interface StoreFrontendUrl {
  url: string;
  env_name?: string;
  frontend_site_id: number;
  is_single_store: boolean;
  source: 'frontend_site_direct' | 'frontend_site_sub_store';
}

export interface StoreFrontendUrlsResponse {
  direct_url: string | null;
  frontend_urls: StoreFrontendUrl[];
}

/**
 * v1.6.X — Wraps `GET /v1/tenant/stores/{store}/frontend-urls`.
 *
 * Per the integration guide: "always fetch fresh; never cache between
 * sessions." Picker components MUST call `getForStore()` on every open
 * so a recently-decommissioned URL doesn't linger in the operator's
 * picker. The service deliberately exposes no cache layer.
 *
 * Empty result is a 200 with `direct_url: null` and `frontend_urls: []`
 * — never treat the absence of URLs as an error.
 */
@Injectable({ providedIn: 'root' })
export class StoreFrontendUrlsService {
  private get base(): string { return `${this.tenant.apiBaseUrl}/v1/tenant`; }

  constructor(private http: HttpClient, private tenant: TenantService) {}

  /** GET /v1/tenant/stores/{storeId}/frontend-urls
   *
   *  v1.6.676 — Defensive response parsing. Originally assumed a wrapped
   *  envelope (`{status, message, data: {direct_url, frontend_urls}}`),
   *  but some backend versions / route configurations return the
   *  payload unwrapped at the top level. Pre-fix, the unwrapped variant
   *  silently parsed to empty arrays → Visit Site button never rendered
   *  even when the store HAD a frontend site. Now we try `data` first,
   *  fall back to top-level if `data.direct_url` and `data.frontend_urls`
   *  are both absent. */
  getForStore(storeId: number): Observable<StoreFrontendUrlsResponse> {
    return this.http.get<any>(`${this.base}/stores/${storeId}/frontend-urls`).pipe(
      map(res => {
        // Prefer the wrapped shape; fall back to the top-level shape if
        // `data` is missing OR if `data` exists but has neither of the
        // two payload keys (defensive against future shape drift).
        const wrapped = res?.data;
        const hasWrappedPayload =
          wrapped && typeof wrapped === 'object' &&
          ('direct_url' in wrapped || 'frontend_urls' in wrapped);
        const d: any = hasWrappedPayload ? wrapped : (res ?? {});
        const out = {
          direct_url: d?.direct_url ?? null,
          frontend_urls: Array.isArray(d?.frontend_urls) ? d.frontend_urls as StoreFrontendUrl[] : [],
        };
        // Diagnostic logging — gated behind a single console.info so it
        // doesn't spam in steady state but is immediately visible when
        // an operator opens devtools to figure out why the button is
        // hidden. Logs both the raw response and the parsed shape.
        // eslint-disable-next-line no-console
        console.info('[StoreFrontendUrls]', {
          storeId,
          parsedFrom: hasWrappedPayload ? 'data.{...}' : 'top-level',
          direct_url: out.direct_url,
          frontend_urls_count: out.frontend_urls.length,
          raw: res,
        });
        return out;
      })
    );
  }
}
